/**
 * Alert <uo-tooltip>
 *
 * Usage
 * <uo-tooltip>
 *  Tooltip content
 *
 *  <uo-button slot="target">Hover</uo-button>
 * </uo-tooltip>
 */

// Import old style (light DOM version)
import './style.scss';

// Import style for web component
import styles from './web-component-style.scss?inline';

// Import tippy
// https://github.com/atomiks/tippyjs
import tippy from 'tippy.js';

// Import dependencies
import { css, html, LitElement, unsafeCSS } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';


// eslint-disable-next-line require-jsdoc
@customElement( 'uo-tooltip' )
export class AutomatorTooltip extends LitElement {
	// Define style
	static styles = css`${ unsafeCSS( styles ) }`;

	// Properties
	// Determines the events that cause the tippy to show. Multiple event names are separated by spaces.
	@property( { type: String } ) accessor trigger = 'mouseenter';

	// The *preferred* placement of the tippy. Note that Popper's flip modifier can change this to the opposite placement if it has more space.
	// Check https://atomiks.github.io/tippyjs/v6/all-props/#placement
	@property( { type: String } ) accessor placement = 'top';

	// Specifies the maximum width of the tippy. Useful to prevent it from being too horizontally wide to read.
	@property( { type: Number, attribute: 'max-width' } ) accessor maxWidth = 350;

	// Determines if the tippy has interactive content inside of it, so that it can be hovered over and clicked inside without hiding.
	@property( { type: Boolean } ) accessor interactive = true;

	// Get the target and tooltip content elements
	@query( '#target' ) accessor $target;

	@query( '#tooltip-content' ) accessor $tooltipContent;

	// eslint-disable-next-line require-jsdoc
	render() {
		return html`
			<div id="target">
				<slot name="target"></slot>
			</div>

			<div id="tooltip-content">
				<slot></slot>
			</div>
		`;
	}

	// eslint-disable-next-line require-jsdoc
	firstUpdated() {
		// Init Tippy
		tippy( this.$target, {
			// The content of the tippy.
			content: this.$tooltipContent,

			// Determines the events that cause the tippy to show. Multiple event names are separated by spaces.
			trigger: this.trigger,
			// Determines if the tippy has interactive content inside of it, so that it can be hovered over and clicked inside without hiding.
			interactive: this.interactive,

			// default (takes reference as an argument)
			// appendTo: () => document.body,

			// Determines if content strings are parsed as HTML instead of text.
			allowHTML: true,
			// Determines if the tippy has an arrow.
			arrow: true,
			// Duration in ms of the transition animation.
			duration: 150,

			// The preferred placement of the tippy. Note that Popper's flip modifier can change this to the opposite placement if it has more space.
			placement: this.placement,

			// Delay in ms once a trigger event is fired before a tippy shows or hides.
			// Possibles values: number, [number, number] = [show, hide].
			// Specifying a number means both the show and hide delays are the same. Use null in the array to use the default value.
			delay: [ 300, 100 ],

			// Determines the time in ms to debounce the interactive hide handler when the cursor leaves the tippy's interactive region.
			// Offers a temporal (rather than spacial) alternative to interactiveBorder, although it can be used in conjunction with it.
			interactiveDebounce: 15,

			// Determines the size of the invisible border around the tippy that will prevent it from hiding if the cursor left it.
			interactiveBorder: 5,

			// Specifies the maximum width of the tippy. Useful to prevent it from being too horizontally wide to read.
			maxWidth: this.maxWidth,
		} );
	}
}
